import type { RouteLocationRaw } from 'vue-router';
import { Router } from '~/core';

/**
 * Navigates to the previous in-app history entry, or to the fallback when the
 * page was landed on directly (vue-router only sets history.state.back for
 * entries it created).
 */
export function goBackOr(fallback: RouteLocationRaw = { name: 'index' }): void {
    if (window.history.state?.back) {
        Router.back();
        return;
    }

    void Router.replace(fallback);
}

export function buildRouteLocation(name: string, params?: Record<string, any>, query?: Record<string, any>): RouteLocationRaw {
    return {
        name,
        params: params || {},
        query: query || {},
    };
}

export function mergeParams(parentParams: Record<string, any>, childPath: string): Record<string, any> {
    const params: Record<string, any> = { ...parentParams };

    // Extract params from child path (e.g., ':server' -> { server: parentParams.server })
    const paramMatches = childPath.match(/:([^/]+)/g);
    if (paramMatches) {
        paramMatches.forEach((match) => {
            const paramName = match.slice(1); // Remove the ':'
            if (parentParams[paramName]) {
                params[paramName] = parentParams[paramName];
            }
        });
    }

    return params;
}

export function extractParams(path: string, params: Record<string, any>): Record<string, any> {
    const extracted: Record<string, any> = {};

    // Extract params from path pattern
    const paramMatches = path.match(/:([^/]+)/g);
    if (paramMatches) {
        paramMatches.forEach((match) => {
            const paramName = match.slice(1);
            if (params[paramName]) {
                extracted[paramName] = params[paramName];
            }
        });
    }

    return extracted;
}

export function generateBreadcrumbPath(routeName: string): string[] {
    if (!routeName) return [];

    const segments = routeName.split('.');
    const breadcrumbs: string[] = [];

    let currentPath = '';
    for (const segment of segments) {
        currentPath = currentPath ? `${currentPath}.${segment}` : segment;
        breadcrumbs.push(currentPath);
    }

    return breadcrumbs;
}
